// ─────────────────────────────────────────────────────────────────────────────
// WP-15 REQ-134 — Sección "Gestión" del Admin: ajustes globales del módulo.
//
// Documento único (GestionSettingsDto) editado inline y persistido con un PUT ATÓMICO
// (v1/admin/settings) — nada persiste sin Guardar, EXCEPTO la imagen de una insignia (mismo patrón
// que Catálogo: sube al instante desde el modal de edición). Incluye: descripción de racha ×7,
// insignias administrables (agregar, desactivar/reactivar, editar en modal con color picker y carga
// real de ícono), toggles globales (switches, no checkboxes) y freeze. Valida i18n ×7 (racha +
// nombres de insignia). Solo AdminGlobal. Estados honestos (loading/error/empty). CERO mocks.
//
// Rediseño 2026-08-05: la pantalla dejó de mostrar las 5 insignias expandidas inline (sobrecargada,
// número fijo). Ahora es una lista compacta (`AdminBadgeList`) + modal de edición
// (`AdminBadgeEditorModal`); el catálogo de insignias es administrable (el backend ya lo permitía —
// StreakBadgeCatalog.cs: "estas cinco son las que la spec fija, no un tope").
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGestionSettings, useSaveGestionSettings, getRecognitionErrorMessage,
  STREAK_PERIODICITIES, toStreakPeriodicity,
  STREAK_MINIMUM_FLOOR, STREAK_MINIMUM_CEILING, clampStreakMinimum,
  POST_EDIT_WINDOWS, toPostEditWindow,
  useRecognitionCatalog,
  useCampaigns,
} from '@/api/recognition';
import type { GestionSettingsDto, AdminBadgeDto } from '@/api/recognition';
import { useUnsavedChangesGuard } from '@/contexts/UnsavedChangesContext';
import { StateBlock } from '../components';
import { AdminSection, AdminSaveBar, AdminToggle, LocalizedFields, hasAllLanguages } from './adminShared';
import { AdminBadgeList } from './AdminBadgeList';
import { AdminBadgeEditorModal } from './AdminBadgeEditorModal';
import { AdminBulkUsers } from './AdminBulkUsers';
import { MisSolicitudesCampana } from './MisSolicitudesCampana';
import { isoToDateInput, dateInputToIso } from './freezeDates';
import { isoToDateTimeLocal, dateTimeLocalToIso, ventanaInvertida } from './datetimeLocal';

/**
 * WP-F5 — Los 8 colores de avatar que la maqueta dibuja en «Configuración de Avatar (Frontline)».
 *
 * 🔴 Son MUESTRAS, no una paleta editable: la maqueta no pinta ningún control que permita cambiarlas
 * —ni añadir, ni quitar, ni elegir—, así que no se modelan en el contrato. Inventar aquí un editor de
 * paleta sería alcance que nadie pidió (R6), y modelar los colores sin editor sería un campo que
 * nadie escribe.
 */
// Los colores viven en `recognition.css` (`.adm-avatar-swatch--1..8`): son ESTÁTICOS, y la guarda
// F8 del programa de paridad exige clase, no estilo inline, para todo lo que no sea un dato en
// vivo. (Y el comentario no escribe el literal: el regex de la guarda lo contaría igual.)
const AVATAR_SWATCHES = [1, 2, 3, 4, 5, 6, 7, 8] as const;

/** Insignia nueva en blanco, con id/code generados en el cliente (mismo `_id` que Mongo usará al
 *  hacer upsert en el Guardar atómico general — ver SaveGestionSettingsAsync). */
function blankBadge(): AdminBadgeDto {
  const id = crypto.randomUUID();
  return {
    id, code: id, nombre: { es: '', en: '', de: '', pt: '', nl: '', fr: '', ro: '' },
    semanasRequeridas: null, iconUrl: null, color: null, isActive: true,
  };
}

export function AdminGestion() {
  const { t } = useTranslation('recognition');
  const query = useGestionSettings();
  const save = useSaveGestionSettings();

  const [draft, setDraft] = useState<GestionSettingsDto | null>(null);
  const [dirty, setDirty] = useState(false);
  useUnsavedChangesGuard(dirty);
  const [editingBadge, setEditingBadge] = useState<AdminBadgeDto | null>(null);
  // WP-F5 — el bloque «Fundamentales disponibles» se pinta desde el CATÁLOGO, no desde las cuatro
  // filas que la maqueta dibuja a mano: un Fundamental añadido después tiene que aparecer aquí solo.
  const catalogo = useRecognitionCatalog();
  /** ids generados en el cliente en esta sesión — controla si el modal deshabilita la imagen. */
  const [newBadgeIds, setNewBadgeIds] = useState<ReadonlySet<string>>(new Set());

  // D8 — Sincroniza el borrador al llegar el documento Y ante refetches en 2º plano, pero SOLO si el
  // usuario no tiene cambios sin guardar (mismo patrón que FundamentalesPanel/BrillamosBannerEditor).
  // Antes sembraba una sola vez (`draft === null`) y NUNCA resincronizaba: con la pestaña abierta un
  // rato la copia quedaba vieja y, como el PUT reemplaza el documento COMPLETO de Gestión, al guardar
  // revertía en silencio los ~52 campos al estado que tenían al abrir la pantalla — el peor de los
  // tres paneles que editan este mismo documento global.
  // RF-037: la periodicidad se sanea AL ENTRAR. Un documento anterior a RF-037 puede traer 'biweekly',
  // valor que ya no existe: sin el saneo el `<select className="field">` no casaría con ninguna opción y el admin
  // acabaría reenviando 'biweekly', que el backend rechaza con 422.
  useEffect(() => {
    setDraft((cur) => (query.data && (cur === null || !dirty)
      ? { ...query.data, periodicity: toStreakPeriodicity(query.data.periodicity) }
      : cur));
  }, [query.data, dirty]);

  const patch = (p: Partial<GestionSettingsDto>) => { setDraft((d) => (d ? { ...d, ...p } : d)); setDirty(true); };

  // ── RN-C08 — el pánico no se ofrece cuando no hay nada de Brillamos que detener ────────────────
  //
  // «Permanece deshabilitado por default hasta que exista al menos una campaña Brillamos Juntos
  // activa en alguna OU». Esta pantalla está gateada a Admin General en el servidor, así que el
  // listado de campañas activas que ve aquí NO viene recortado por alcance: «en alguna OU» y «en las
  // que puedo ver» son lo mismo para este usuario. pageSize holgado por la misma razón que
  // `BrillamosConfigInline`: el backend no permite más de una campaña activa por OU (RN-C05), así que
  // 100 no pagina en la práctica y evita concluir «no hay ninguna» porque la que hay cayó en la
  // página 2.
  //
  // 🔴 APAGARLO SIEMPRE SE PUEDE. La regla condiciona ENCENDER, no soltar: si el pánico quedó activo
  // y las campañas terminaron después, deshabilitar el interruptor dejaría el envío bloqueado para
  // siempre sin forma de revertirlo desde la interfaz. Es el escenario exacto que Alejandro recorrió
  // en la sesión de UAT (encendió el pánico, detuvo la campaña de FOSE y siguió trabajando en
  // Reconocer), así que no es hipotético.
  const campanasActivas = useCampaigns('Activa', 1, 100);
  const hayBrillamosActiva = (campanasActivas.data?.items ?? []).some((c) => c.tipo === 'Brillamos');
  const panicoEncendido = draft?.sendingDisabled ?? false;
  const panicoAccionable = hayBrillamosActiva || panicoEncendido;

  const streakOk = draft ? hasAllLanguages(draft.streakDescription) : false;
  // Una insignia recién agregada y sin terminar no debe bloquear el Guardar general para siempre:
  // desactivarla (isActive === false) exime el requisito de i18n ×7 — es la única forma que tiene el
  // admin de "salir" de una insignia en blanco sin completarla ahora mismo.
  const badgesOk = draft ? draft.badges.every((b) => !b.isActive || hasAllLanguages(b.nombre)) : false;
  // WP-E4 — el backend rechaza la ventana invertida con 422 (SaveGestionSettingsCommandHandler). Se
  // bloquea también aquí para que el admin lo vea al teclearla y no después de perder el guardado:
  // el PUT es ATÓMICO, así que un 422 por las dos fechas tira al suelo todo lo demás que hubiera
  // editado en la pantalla.
  const ventanaInvertidaAhora = ventanaInvertida(draft?.antiSpoilerFrom, draft?.antiSpoilerTo);
  const canSave = streakOk && badgesOk && !ventanaInvertidaAhora;

  // RF-040 — Las fechas del freeze se convierten AL EDITARLAS (ver el `onChange` de los dos
  // `<input type="date">`), no aquí. Antes se reconvertían en cada guardado, así que un cambio en
  // el mínimo semanal o en el nombre de una insignia bastaba para desplazar la ventana de freeze
  // sin que nadie la hubiera tocado. Lo que no se edita viaja tal cual llegó del servidor.
  const doSave = () => {
    if (!draft) return;
    save.mutate(draft, { onSuccess: () => { setDirty(false); setNewBadgeIds(new Set()); } });
  };

  const handleAddBadge = () => {
    const created = blankBadge();
    setDraft((d) => (d ? { ...d, badges: [...d.badges, created] } : d));
    setNewBadgeIds((ids) => new Set(ids).add(created.id));
    setDirty(true);
    setEditingBadge(created);
  };

  /**
   * CPE-150 — quita la insignia del BORRADOR. Nada se borra hasta Guardar, igual que el resto de esta
   * pantalla; el PUT reemplaza el documento y el backend elimina de `badges_config` las que ya no
   * vengan en la lista (ver `GamificationRepository.SaveGestionSettingsAsync`).
   *
   * También sale de `newBadgeIds`: si era una recién agregada y no se limpiara, ese id quedaría
   * marcado como "nueva" para siempre y confundiría al modal si el admin volviera a crear una.
   */
  const handleDeleteBadge = (badge: AdminBadgeDto) => {
    setDraft((d) => (d ? { ...d, badges: d.badges.filter((b) => b.id !== badge.id) } : d));
    setNewBadgeIds((ids) => { const next = new Set(ids); next.delete(badge.id); return next; });
    setEditingBadge((cur) => (cur?.id === badge.id ? null : cur));
    setDirty(true);
  };

  const handleSaveBadgeFromModal = (updated: AdminBadgeDto) => {
    setDraft((d) => (d ? { ...d, badges: d.badges.map((b) => (b.id === updated.id ? updated : b)) } : d));
    setDirty(true);
    setEditingBadge(null);
  };

  /** Propaga el iconUrl recién subido al draft del padre SIN cerrar el modal — la carga de imagen
   *  persiste al instante (ver AdminBadgeEditorModal.handleUpload), pero no es un "Guardar cambios"
   *  del admin, así que no debe interrumpir la edición en curso. */
  const handleBadgeIconUploaded = (updated: AdminBadgeDto) => {
    setDraft((d) => (d ? { ...d, badges: d.badges.map((b) => (b.id === updated.id ? updated : b)) } : d));
    setDirty(true);
  };

  return (
    <>
    <AdminSection title={t('admin.management.title')} description={t('admin.management.descLong')}>
      <StateBlock
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        isEmpty={!draft}
        emptyText={t('common.empty')}
        onRetry={() => { query.refetch(); }}
      >
        {draft && (
          <>
            <AdminSaveBar onSave={doSave} saving={save.isPending} dirty={dirty && canSave} saved={save.isSuccess}
              disabledReason={!canSave ? t('admin.management.fixValidation') : null} />
            {save.isError && <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(save.error, t('admin.saveError'))}</p>}

            {/* ── WP-F5 · «Fundamentales disponibles» — el PRIMER bloque de la maqueta ────────────
                Se pinta desde el CATÁLOGO y no desde las cuatro filas que la maqueta dibuja a mano:
                un Fundamental añadido después tiene que salir aquí sin tocar código.

                🔴 El modelo es una lista de EXCLUIDOS. Con una de permitidos, lo que se añadiera al
                catálogo después nacería apagado y el documento guardado ni lo nombraría. */}
            <h4 className="adm-sub-title"><i className="fas fa-list-check" /> {t('admin.management.fundamentalsTitle')}</h4>
            <p className="adm-note">{t('admin.management.fundamentalsDesc')}</p>
            {catalogo.data && catalogo.data.length > 0 && (() => {
              const retirados = draft.fundamentalesDeshabilitados ?? [];
              const disponibles = catalogo.data.filter((c) => !retirados.includes(c.id));
              return (
                <div className="adm-fund-list">
                  {catalogo.data.map((f) => {
                    const disponible = !retirados.includes(f.id);
                    // Sin este tope un admin puede dejar cero disponibles y NADIE podría reconocer. El
                    // servidor también lo rechaza —la regla vive ahí—, pero llegar al 422 después de
                    // apagar el último es una forma pésima de enterarse.
                    const esElUltimo = disponible && disponibles.length === 1;
                    return (
                      <label key={f.id} className="adm-fund-item">
                        <span className="adm-fund-dot" style={{ background: f.color ?? 'var(--cp-purple)' }} aria-hidden />
                        <span className="adm-fund-name">{f.names?.es ?? f.code}</span>
                        <AdminToggle
                          isOn={disponible}
                          disabled={esElUltimo}
                          onChange={(on) => patch({
                            fundamentalesDeshabilitados: on
                              ? retirados.filter((id) => id !== f.id)
                              : [...retirados, f.id],
                          })}
                        />
                      </label>
                    );
                  })}
                  {disponibles.length === 1 && (
                    <p className="adm-note adm-note--info">
                      <i className="fas fa-circle-info" /> {t('admin.management.fundamentalsLastOne')}
                    </p>
                  )}
                </div>
              );
            })()}

            {/* UAT #71 — falta separación visual entre «Fundamentales disponibles» y este bloque: era
                el único tramo de la pantalla sin `h4.adm-sub-title` propio, así que quedaba pegado
                al bloque anterior sin el margen de 22px que sí reciben el resto de secciones.
                Spacer puro, sin encabezado nuevo: el bug es de espaciado, no de contenido. */}
            <div className="adm-gestion-loose-block">
              {/* Descripción de racha ×7 */}
              <LocalizedFields label={t('admin.management.streakDescription')} value={draft.streakDescription} onChange={(streakDescription) => patch({ streakDescription })} textarea requireAll translatable />
            </div>
            {!streakOk && <p className="adm-note adm-note--warn"><i className="fas fa-language" /> {t('admin.management.needAllLangs')}</p>}

            {/* Toggles globales */}
            <div className="adm-field">
              <span>{t('admin.management.globalToggles')}</span>
              <div className="adm-toggles-row">
                <label className="adm-inline-toggle">
                  <AdminToggle isOn={draft.streakEnabled} onChange={(streakEnabled) => patch({ streakEnabled })} /> {t('admin.management.streakEnabled')}
                </label>
                <label className="adm-inline-toggle">
                  <AdminToggle isOn={draft.multinomination} onChange={(multinomination) => patch({ multinomination })} /> {t('admin.management.multinomination')}
                </label>
                <label className="adm-inline-toggle">
                  <AdminToggle isOn={draft.dailyHabitsMandatory} onChange={(dailyHabitsMandatory) => patch({ dailyHabitsMandatory })} /> {t('admin.management.dailyHabitsMandatory')}
                </label>
                {/* El Anti-Spoiler ya NO está en esta fila: tiene bloque propio más abajo, con su
                    ventana Desde/Hasta, como en la maqueta (`fundamentales-admin.js:1163`). */}
                <label className="adm-inline-toggle">
                  <AdminToggle isOn={draft.wrappedByOu} onChange={(wrappedByOu) => patch({ wrappedByOu })} /> {t('admin.management.wrappedByOu')}
                </label>
                <label className="adm-inline-toggle">
                  <AdminToggle isOn={draft.reactionsEnabled ?? true} onChange={(reactionsEnabled) => patch({ reactionsEnabled })} /> {t('admin.management.reactionsEnabled')}
                </label>
                <label className="adm-inline-toggle">
                  <AdminToggle isOn={draft.allowPromoteToBrillamos ?? false} onChange={(allowPromoteToBrillamos) => patch({ allowPromoteToBrillamos })} /> {t('admin.management.allowPromoteToBrillamos')}
                </label>
                {/* La edición post-envío ya NO está en esta fila: tiene bloque propio más abajo, con
                    su ventana, como en la maqueta (`fundamentales-admin.js:1163`). */}
                <label className="adm-inline-toggle">
                  <AdminToggle isOn={draft.streakPerPersonCapEnabled ?? true} onChange={(streakPerPersonCapEnabled) => patch({ streakPerPersonCapEnabled })} /> {t('admin.management.streakPerPersonCapEnabled')}
                </label>
              </div>
            </div>

            {/* DEFAULT GLOBAL de las reglas de envío (baseline). Escribe el doc GLOBAL (GestionSettings);
                Fundamentales y Brillamos heredan estos valores salvo que cada tab los overridee en su
                propio doc de programa. Mapeo 1:1 con los campos globales; los tabs guardan aparte. */}
            <div className="adm-field">
              <span>{t('admin.campaigns.rulesTitle')}</span>
              <div className="adm-toggles-row">
                <label className="adm-inline-toggle"><AdminToggle isOn={draft.multiFundamental ?? true} onChange={(multiFundamental) => patch({ multiFundamental })} /> {t('admin.campaigns.ruleMultiFundamental')}</label>
                <label className="adm-inline-toggle"><AdminToggle isOn={draft.commentRequired ?? false} onChange={(commentRequired) => patch({ commentRequired })} /> {t('admin.campaigns.commentRequired')}</label>
                <label className="adm-inline-toggle"><AdminToggle isOn={draft.bulkCsvEnabled ?? true} onChange={(bulkCsvEnabled) => patch({ bulkCsvEnabled })} /> {t('admin.campaigns.ruleBulkCsv')}</label>
                <label className="adm-inline-toggle"><AdminToggle isOn={draft.permitirAnonimos ?? true} onChange={(permitirAnonimos) => patch({ permitirAnonimos })} /> {t('admin.campaigns.ruleAnonymousEnabled')}</label>
                <label className="adm-inline-toggle"><AdminToggle isOn={draft.limitsEnabled ?? true} onChange={(limitsEnabled) => patch({ limitsEnabled })} /> {t('admin.campaigns.limitsActive')}</label>
                <label className="adm-inline-toggle"><AdminToggle isOn={draft.recognitionsLimitActive ?? true} onChange={(recognitionsLimitActive) => patch({ recognitionsLimitActive })} /> {t('admin.campaigns.limitRecognitions')}</label>
                <label className="adm-inline-toggle"><AdminToggle isOn={draft.fundamentalsLimitActive ?? true} onChange={(fundamentalsLimitActive) => patch({ fundamentalsLimitActive })} /> {t('admin.campaigns.limitFundamentals')}</label>
              </div>
            </div>
            <div className="adm-form-grid">
              <label className="adm-field"><span>{t('admin.campaigns.ruleMinChars')}</span>
                <input type="number" min={0} max={500} step={1} value={draft.minCommentChars ?? 20} onChange={(e) => patch({ minCommentChars: Math.max(0, Math.min(500, Math.round(Number(e.target.value) || 0))) })} />
              </label>
              <label className="adm-field"><span>{t('admin.campaigns.limitFundamentals')}</span>
                <input type="number" min={1} max={50} step={1} value={draft.maxFundamentalsPerRecognition ?? 2} onChange={(e) => patch({ maxFundamentalsPerRecognition: Math.max(1, Math.min(50, Math.round(Number(e.target.value) || 1))) })} />
              </label>
              <label className="adm-field"><span>{t('admin.campaigns.limitRecognitions')}</span>
                <input type="number" min={1} max={1000} step={1} value={draft.maxRecognitionsPerPeriod ?? 5} onChange={(e) => patch({ maxRecognitionsPerPeriod: Math.max(1, Math.min(1000, Math.round(Number(e.target.value) || 1))) })} />
              </label>
              <label className="adm-field"><span>{t('admin.campaigns.limitFrequency')}</span>
                <select className="field" value={draft.recognitionsPeriod ?? 'weekly'} onChange={(e) => patch({ recognitionsPeriod: e.target.value })}>
                  <option value="weekly">{t('admin.campaigns.periodWeekly')}</option>
                  <option value="monthly">{t('admin.campaigns.periodMonthly')}</option>
                  <option value="daily">{t('admin.campaigns.periodDaily')}</option>
                </select>
              </label>
            </div>

            {/* Racha: mínimo semanal, periodicidad, freeze */}
            <div className="adm-form-grid">
              <label className="adm-field"><span>{t('admin.management.weeklyMinimum')}</span>
                <input type="number" min={STREAK_MINIMUM_FLOOR} max={STREAK_MINIMUM_CEILING} value={draft.weeklyMinimum} onChange={(e) => patch({ weeklyMinimum: clampStreakMinimum(Number(e.target.value) || STREAK_MINIMUM_FLOOR) })} />
              </label>
              <label className="adm-field"><span>{t('admin.management.periodicity')}</span>
                <select className="field" value={draft.periodicity} onChange={(e) => patch({ periodicity: toStreakPeriodicity(e.target.value) })}>
                  {STREAK_PERIODICITIES.map((p) => <option key={p} value={p}>{t(`admin.management.period.${p}`)}</option>)}
                </select>
              </label>
              {/* REQ-043 — tope de envíos 1:1 a la misma persona que cuentan para la racha (grupales siempre cuentan).
                  El número solo aplica si el toggle streakPerPersonCapEnabled está activo. */}
              <label className="adm-field"><span>{t('admin.management.streakPerPersonCap')}</span>
                <input type="number" min={1} max={50} value={draft.streakPerPersonCap ?? 2}
                  disabled={!(draft.streakPerPersonCapEnabled ?? true)}
                  onChange={(e) => patch({ streakPerPersonCap: Math.min(50, Math.max(1, Math.trunc(Number(e.target.value)) || 1)) })} />
              </label>
              <label className="adm-field"><span>{t('admin.management.freezeFrom')}</span>
                <input type="date" value={isoToDateInput(draft.freezeFrom)} onChange={(e) => patch({ freezeFrom: dateInputToIso(e.target.value) })} />
              </label>
              <label className="adm-field"><span>{t('admin.management.freezeTo')}</span>
                <input type="date" value={isoToDateInput(draft.freezeTo)} onChange={(e) => patch({ freezeTo: dateInputToIso(e.target.value) })} />
              </label>
            </div>
            <p className="adm-note"><i className="fas fa-circle-info" /> {t('admin.management.periodicityHint')}</p>
            {/* Con Mensual la racha cuenta MESES y el vocabulario del empleado ya lo refleja. Lo que
                queda abierto es de producto, no de código: RF-038 define los umbrales de insignia en
                semanas (1/4/8/12/20), y el tope anti-abuso de REQ-043 ("2 por persona y semana") pasa
                a aplicarse por mes. Quien puede activarlo tiene que saber ambas cosas. */}
            {draft.periodicity === 'monthly' && (
              <p className="adm-note adm-note--warn" role="alert">
                <i className="fas fa-triangle-exclamation" /> {t('admin.management.periodicityMonthlyWarning')}
              </p>
            )}

            <AdminBadgeList badges={draft.badges} onAdd={handleAddBadge} onEdit={setEditingBadge} onDelete={handleDeleteBadge} />

            {/* ── T1 / RF-107 · Edición de Reconocimientos Enviados ────────────────────────────
                1:1 con la maqueta (`fundamentales-admin.js:1163`): título con icono, descripción y
                la fila con «Permitir edición» + «Ventana de edición». Va justo antes del bloque de
                Anti-Spoiler, como en la maqueta. */}
            <h4 className="adm-sub-title"><i className="fas fa-pen-to-square" /> {t('admin.management.postEditTitle')}</h4>
            <p className="adm-note">{t('admin.management.postEditDesc')}</p>
            <div className="adm-form-grid">
              <label className="adm-inline-toggle">
                <AdminToggle isOn={draft.allowPostEdit ?? false} onChange={(allowPostEdit) => patch({ allowPostEdit })} /> {t('admin.management.allowPostEdit')}
              </label>
              <label className="adm-field"><span>{t('admin.management.postEditWindow')}</span>
                {/* Deshabilitado con la edición apagada: un plazo para algo que no se puede hacer no
                    significa nada, y ofrecerlo invita a creer que enciende la edición. */}
                <select className="field"
                  value={toPostEditWindow(draft.postEditWindow)}
                  disabled={!(draft.allowPostEdit ?? false)}
                  onChange={(e) => patch({ postEditWindow: toPostEditWindow(e.target.value) })}
                >
                  {POST_EDIT_WINDOWS.map((w) => (
                    <option key={w} value={w}>{t(`admin.management.postEditWindowOpt.${w}`)}</option>
                  ))}
                </select>
              </label>
            </div>

            {/* ── WP-F5 · «Configuración de Avatar (Frontline)» ────────────────────────────────
                Los dos interruptores de la maqueta. La paleta se pinta como MUESTRA, sin control:
                la maqueta no ofrece ninguno y añadirlo sería alcance inventado (R6). */}
            <h4 className="adm-sub-title"><i className="fas fa-user-circle" /> {t('admin.management.avatarTitle')}</h4>
            <p className="adm-note">{t('admin.management.avatarDesc')}</p>
            <div className="adm-form-grid">
              <label className="adm-inline-toggle">
                <AdminToggle isOn={draft.avatarFrontlineEnabled ?? true} onChange={(avatarFrontlineEnabled) => patch({ avatarFrontlineEnabled })} /> {t('admin.management.avatarEnabled')}
              </label>
              <label className="adm-inline-toggle">
                {/* Sin avatar personalizable, ofrecer el selector post-registro no significa nada. */}
                <AdminToggle
                  isOn={draft.avatarSelectorPostRegistro ?? true}
                  disabled={!(draft.avatarFrontlineEnabled ?? true)}
                  onChange={(avatarSelectorPostRegistro) => patch({ avatarSelectorPostRegistro })}
                /> {t('admin.management.avatarSelector')}
              </label>
            </div>
            <div className="adm-avatar-palette">
              <span className="adm-note">{t('admin.management.avatarColors')}</span>
              {AVATAR_SWATCHES.map((n) => <span key={n} className={`adm-avatar-swatch adm-avatar-swatch--${n}`} />)}
            </div>

            {/* ── WP-F5 · «Descripción configurable del campo de texto» ─────────────────────────
                🔴 SIN `requireAll`, al revés que la descripción de racha: un idioma sin texto cae al
                i18n del producto, y exigir los 7 bloquearía el Guardar de OTRAS pantallas que
                comparten este mismo documento — el desacople que el handler ya documenta. */}
            <h4 className="adm-sub-title"><i className="fas fa-comment-dots" /> {t('admin.management.placeholderTitle')}</h4>
            <p className="adm-note">{t('admin.management.placeholderDesc')}</p>
            <LocalizedFields
              label={t('admin.management.placeholderLabel')}
              value={draft.messagePlaceholder ?? {}}
              onChange={(messagePlaceholder) => patch({ messagePlaceholder })}
              textarea
              translatable
            />

            {/* ── WP-F5 · «Botón de Pánico — Brillamos Juntos» ──────────────────────────────────
                🔴 NO es un interruptor nuevo: es EL MISMO `sendingDisabled` que ya se aplica y que
                también se edita en Campañas → Fundamentales → Reglas de Envío. La maqueta lo dibuja
                en los dos sitios, y duplicar el CAMPO habría creado dos verdades para una pregunta.
                Aquí se dice de dónde más se toca, para que nadie crea que son cosas distintas.

                Esta pantalla entera está gateada a Admin General en el servidor, que es justo lo que
                la maqueta exige de este bloque. */}
            <h4 className="adm-sub-title adm-sub-title--danger"><i className="fas fa-power-off" /> {t('admin.management.panicTitle')}</h4>
            <p className="adm-note">{t('admin.management.panicDesc')}</p>
            <div className="adm-panic">
              <label className="adm-inline-toggle adm-inline-toggle--warn">
                <AdminToggle
                  isOn={panicoEncendido}
                  disabled={!panicoAccionable}
                  onChange={(sendingDisabled) => patch({ sendingDisabled })}
                /> {t('admin.management.panicToggle')}
              </label>
              {!panicoAccionable && (
                <p className="adm-note adm-note--info">
                  <i className="fas fa-circle-info" /> {t('admin.management.panicNoActiveCampaign')}
                </p>
              )}
              {(draft.sendingDisabled ?? false) && (
                <p className="adm-note adm-note--warn" role="alert">
                  <i className="fas fa-triangle-exclamation" /> {t('admin.management.panicActiveHint')}
                </p>
              )}
              <p className="adm-note adm-note--info"><i className="fas fa-circle-info" /> {t('admin.management.panicSameSwitchHint')}</p>
            </div>

            {/* ── WP-E4 / RF-168 · Modo Anti-Spoiler (Premiación) ──────────────────────────────
                1:1 con la maqueta (`fundamentales-admin.js:1163`): título con icono, descripción,
                tarjeta enmarcada con toggle + Desde + Hasta y la nota informativa.

                Sale de la fila de "Toggles globales" porque ahí no cabía su ventana, y porque el
                bloque de la maqueta es una tarjeta propia. Es GLOBAL, no por OU: la maqueta no tiene
                dimensión de OU en este bloque (decisión P9 del traspaso). */}
            <h4 className="adm-sub-title"><i className="fas fa-eye-slash" /> {t('admin.management.antiSpoilerTitle')}</h4>
            <p className="adm-note">{t('admin.management.antiSpoilerDesc')}</p>
            <div className="adm-antispoiler">
              <div className="adm-form-grid">
                <label className="adm-inline-toggle">
                  <AdminToggle isOn={draft.antiSpoiler} onChange={(antiSpoiler) => patch({ antiSpoiler })} /> {t('admin.management.antiSpoilerActivo')}
                </label>
                <label className="adm-field"><span>{t('admin.management.antiSpoilerFrom')}</span>
                  <input type="datetime-local" value={isoToDateTimeLocal(draft.antiSpoilerFrom)}
                    onChange={(e) => patch({ antiSpoilerFrom: dateTimeLocalToIso(e.target.value) })} />
                </label>
                <label className="adm-field"><span>{t('admin.management.antiSpoilerTo')}</span>
                  <input type="datetime-local" value={isoToDateTimeLocal(draft.antiSpoilerTo)}
                    onChange={(e) => patch({ antiSpoilerTo: dateTimeLocalToIso(e.target.value) })} />
                </label>
              </div>
              <p className="adm-note adm-note--info"><i className="fas fa-circle-info" /> {t('admin.management.antiSpoilerHint')}</p>
              {/* Sin fechas el modo protege mientras el toggle esté encendido. Merece decirlo: quien
                  abre esta pantalla el día de la premiación necesita saber si ya está protegido. */}
              {draft.antiSpoiler && !draft.antiSpoilerFrom && !draft.antiSpoilerTo && (
                <p className="adm-note adm-note--info"><i className="fas fa-clock" /> {t('admin.management.antiSpoilerSinVentana')}</p>
              )}
            </div>
            {/* 🔴 Una ventana invertida NO es media configuración: es una que no protege NUNCA. El
                backend la rechaza al guardar, pero el aviso tiene que estar aquí — sin él el admin
                ve el toggle encendido, se va tranquilo, y el modo está apagado de hecho. */}
            {ventanaInvertidaAhora && (
              <p className="adm-note adm-note--warn" role="alert">
                <i className="fas fa-triangle-exclamation" /> {t('admin.management.antiSpoilerVentanaInvertida')}
              </p>
            )}

            <AdminSaveBar onSave={doSave} saving={save.isPending} dirty={dirty && canSave} saved={save.isSuccess}
              disabledReason={!canSave ? t('admin.management.fixValidation') : null} />
          </>
        )}
      </StateBlock>
    </AdminSection>
    <AdminBadgeEditorModal
      badge={editingBadge}
      isNew={editingBadge !== null && newBadgeIds.has(editingBadge.id)}
      onSave={handleSaveBadgeFromModal}
      onIconUploaded={handleBadgeIconUploaded}
      onClose={() => setEditingBadge(null)}
    />
    {/* UAT #71 — sin este envoltorio, la tarjeta de Carga masiva quedaba pegada a la barra de
        Guardar/aviso de validación de arriba: son dos piezas sueltas (dos `.adm-section`/componentes
        independientes) fuera de cualquier contenedor con `gap`, y `.adm-section` no trae margen
        propio arriba ni abajo. */}
    <div className="adm-gestion-loose-block">
      <AdminBulkUsers />
    </div>
    {/* WP-B5 (RN-C11) — «Mis Solicitudes de Campaña»: la contraparte de la bandeja del Admin Global.
        Va en Gestión y no en Campañas porque es del SOLICITANTE, no de la administración de la
        campaña: quien la mira puede no tener ninguna campaña propia viva.

        UAT #71 — mismo motivo que arriba: su propio `h4.adm-sub-title` pierde el margen superior
        por ser `:first-child` de SU sección (regla pensada para cuando el título es lo primero
        dentro de `AdminSection`, no para dos piezas sueltas apiladas una tras otra). */}
    <div className="adm-gestion-loose-block">
      <MisSolicitudesCampana />
    </div>
    </>
  );
}

export default AdminGestion;
